import { useQuery } from "@tanstack/react-query";

import { Label } from "@/components/ui/Input";

import {
  fetchQualitySavedAddresses,
  type QualitySavedAddress,
} from "./rmaApi";
import type { QualityRmaAddressValue } from "./QualityRmaAddressFields";

export function QualityRmaSavedAddressPicker({
  id,
  customerFsAccountId,
  endUserFsAccountId,
  disabled = false,
  onApply,
}: {
  id: string;
  customerFsAccountId: number | null;
  endUserFsAccountId: number | null;
  disabled?: boolean;
  onApply: (next: QualityRmaAddressValue) => void;
}): JSX.Element {
  const hasParty = customerFsAccountId != null || endUserFsAccountId != null;
  const listQuery = useQuery({
    queryKey: ["quality", "saved-addresses", customerFsAccountId, endUserFsAccountId],
    queryFn: () =>
      fetchQualitySavedAddresses({
        customerFsAccountId,
        endUserFsAccountId,
      }),
    enabled: hasParty,
    staleTime: 30_000,
  });
  const rows = listQuery.data?.rows ?? [];

  if (!hasParty) {
    return (
      <p className="md:col-span-2 text-sm text-slate-600">
        Pick a customer or end user to fill from a saved address, or type the address below.
      </p>
    );
  }

  if (listQuery.isLoading) {
    return <p className="md:col-span-2 text-sm text-slate-600">Loading saved addresses.</p>;
  }

  if (rows.length === 0) {
    return (
      <p className="md:col-span-2 text-sm text-slate-600">
        No saved addresses for the selected customer or end user. Type the address below.
      </p>
    );
  }

  return (
    <div className="md:col-span-2">
      <Label htmlFor={id}>Fill from saved address</Label>
      <select
        id={id}
        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
        defaultValue=""
        disabled={disabled}
        onChange={(event) => {
          const selected = rows.find((row) => row.id === event.target.value);
          event.target.value = "";
          if (selected == null) return;
          onApply(savedAddressToRmaValue(selected));
        }}
      >
        <option value="">Type a manual address</option>
        {rows.map((row) => (
          <option key={row.id} value={row.id}>
            {optionLabel(row)}
          </option>
        ))}
      </select>
    </div>
  );
}

function savedAddressToRmaValue(row: QualitySavedAddress): QualityRmaAddressValue {
  return {
    line1: row.line1,
    line2: row.line2,
    city: row.city,
    state: row.state,
    postalCode: row.postalCode,
    country: row.country ?? undefined,
  };
}

function optionLabel(row: QualitySavedAddress): string {
  const street = [row.line1, row.city, row.state, row.postalCode].filter((part) => part.trim() !== "").join(", ");
  return street === "" ? row.label : `${row.label}: ${street}`;
}
